export type InlineAttachment = {
  id: string;
  url: string;
  type: string;
  fileType: 'image' | 'video' | 'audio';
  name?: string;
  /** Duration in seconds (video/audio only) */
  duration?: number;
  /** Frame role for image attachments (start = first frame, end = last frame, reference) */
  role?: 'start' | 'end' | 'reference';
  /** Preview URL for Asset:// references */
  previewUrl?: string;
};

/** Classify a ReactFlow node as image/video/audio based on type and data. */
export function classifyNodeMediaType(n: any): 'image' | 'video' | 'audio' | null {
  if (n?.type === 'image') return 'image';
  if (n?.type === 'video') return 'video';
  if (n?.type === 'audio') return 'audio';
  if (n?.type === 'upload') {
    const ft = n.data?.fileType;
    const ct = n.data?.content?.type as string | undefined;
    if (ft === 'image' || ct?.startsWith('image/')) return 'image';
    if (ft === 'video' || ct?.startsWith('video/')) return 'video';
    if (ft === 'audio' || ct?.startsWith('audio/')) return 'audio';
  }
  return null;
}

export function detectMediaType(mimeType: string): InlineAttachment['fileType'] {
  const mt = String(mimeType || '').toLowerCase();
  if (mt.startsWith('video/')) return 'video';
  if (mt.startsWith('audio/')) return 'audio';
  return 'image';
}

// --------------- Inline Media Config ---------------

export type InlineMediaTypeConfig = {
  allow: boolean;
  maxCount: number;
  /** e.g. "50MB", "200MB" */
  maxSize: string;
  /** e.g. "1s", "3s" — minimum duration for video/audio */
  minDuration?: string;
  /** e.g. "60s", "300s" — maximum duration for video/audio */
  maxDuration?: string;
  /** Minimum total pixels (width × height) for video files */
  minPixels?: number;
  /** Maximum total pixels (width × height) for video files */
  maxPixels?: number;
  /** Accepted MIME types, e.g. ['image/png','image/jpeg','image/webp'] */
  formats?: string[];
  /** Show a dedicated labelled placeholder slot in the grid. true = default type label, string = custom label */
  slot?: boolean | string;
};

export type FrameImageConfig = {
  /** e.g. "10MB" */
  maxSize?: string;
  /** Maximum total pixels (width × height) */
  maxPixels?: number;
  /** Accepted MIME types, e.g. ['image/png','image/jpeg'] */
  formats?: string[];
};

export type InlineMediaConfig = {
  allowInline: boolean;
  /** When true, over-limit media is kept as greyed-out candidates instead of being rejected. Defaults to true. */
  allowCandidates?: boolean;
  image?: InlineMediaTypeConfig;
  video?: InlineMediaTypeConfig;
  audio?: InlineMediaTypeConfig;
  /** Overrides for first-frame uploads (falls back to image config) */
  firstFrame?: FrameImageConfig;
  /** Overrides for last-frame uploads (falls back to image config) */
  lastFrame?: FrameImageConfig;
  /** video + audio combined max count */
  maxMediaCount?: number;
  /** image + video + audio combined max count */
  maxTotalCount?: number;
  mediaDurationLimit?: {
    enabled: boolean;
    /** e.g. "300s" */
    maxTotalDuration: string;
  };
};

const MEDIA_DISALLOW: InlineMediaTypeConfig = { allow: false, maxCount: 0, maxSize: '0' };

/** Generate sensible inlineMedia defaults from frameMode when the model has no explicit config. */
export function defaultInlineMediaForMode(frameMode: string | undefined): InlineMediaConfig {
  switch (frameMode) {
    case 'first':
      return { allowInline: true, image: { allow: true, maxCount: 1, maxSize: '30MB' }, video: MEDIA_DISALLOW, audio: MEDIA_DISALLOW, maxTotalCount: 1 };
    case 'first_last':
      return { allowInline: true, image: { allow: true, maxCount: 2, maxSize: '30MB' }, video: MEDIA_DISALLOW, audio: MEDIA_DISALLOW, maxTotalCount: 2 };
    case 'ref':
      return { allowInline: true, image: { allow: true, maxCount: 9, maxSize: '30MB' }, video: { allow: true, maxCount: 3, maxSize: '50MB' }, audio: { allow: true, maxCount: 3, maxSize: '15MB' }, maxTotalCount: 12 };
    case 'avatar':
      return { allowInline: true, image: { allow: true, maxCount: 1, maxSize: '30MB', slot: true }, video: MEDIA_DISALLOW, audio: { allow: true, maxCount: 1, maxSize: '15MB', slot: true }, maxTotalCount: 2 };
    case 'v2v':
      return { allowInline: true, image: MEDIA_DISALLOW, video: { allow: true, maxCount: 1, maxSize: '100MB' }, audio: MEDIA_DISALLOW, maxTotalCount: 1 };
    default: // 'none' or undefined
      return { allowInline: false, image: MEDIA_DISALLOW, video: MEDIA_DISALLOW, audio: MEDIA_DISALLOW, maxTotalCount: 0 };
  }
}

/** Deep-merge a per-mode inlineMedia override into the base config. */
export function mergeInlineMedia(
  base: InlineMediaConfig | undefined,
  override: Record<string, any> | undefined,
): InlineMediaConfig | undefined {
  if (!override) return base;
  if (!base) return override as InlineMediaConfig;
  return {
    ...base,
    ...override,
    image: override.image ? { ...base.image, ...override.image } as InlineMediaConfig['image'] : base.image,
    video: override.video ? { ...base.video, ...override.video } as InlineMediaConfig['video'] : base.video,
    audio: override.audio ? { ...base.audio, ...override.audio } as InlineMediaConfig['audio'] : base.audio,
    firstFrame: override.firstFrame ? { ...base.firstFrame, ...override.firstFrame } as InlineMediaConfig['firstFrame'] : base.firstFrame,
    lastFrame: override.lastFrame ? { ...base.lastFrame, ...override.lastFrame } as InlineMediaConfig['lastFrame'] : base.lastFrame,
    mediaDurationLimit: override.mediaDurationLimit ? { ...base.mediaDurationLimit, ...override.mediaDurationLimit } as InlineMediaConfig['mediaDurationLimit'] : base.mediaDurationLimit,
  };
}

const SIZE_UNITS: Record<string, number> = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };

/** Parse "50MB" → bytes, "10KB" → bytes. Returns Infinity on failure. */
export function parseBytes(s: string | undefined): number {
  if (!s) return Infinity;
  const m = /^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB)$/i.exec(s.trim());
  if (!m) return Infinity;
  return parseFloat(m[1]) * (SIZE_UNITS[m[2].toUpperCase()] ?? 1);
}

/** Parse "60s" → seconds, "5m" → seconds. Returns Infinity on failure. */
export function parseSeconds(s: string | undefined): number {
  if (!s) return Infinity;
  const sec = /^(\d+(?:\.\d+)?)\s*s$/i.exec(s.trim());
  if (sec) return parseFloat(sec[1]);
  const min = /^(\d+(?:\.\d+)?)\s*m$/i.exec(s.trim());
  if (min) return parseFloat(min[1]) * 60;
  return Infinity;
}

/** Read video pixel dimensions (width × height) via a temporary HTML video element. Returns { width, height } or null on failure/timeout. */
export function getVideoPixels(file: File, timeoutMs = 8000): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) { resolve(null); return; }
    let settled = false;
    const done = (v: { width: number; height: number } | null) => { if (settled) return; settled = true; clearTimeout(timer); URL.revokeObjectURL(url); resolve(v); };
    const el = document.createElement('video');
    const url = URL.createObjectURL(file);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      const w = el.videoWidth;
      const h = el.videoHeight;
      done(w > 0 && h > 0 ? { width: w, height: h } : null);
    };
    el.onerror = () => done(null);
    const timer = setTimeout(() => done(null), timeoutMs);
    el.src = url;
  });
}

/** Read media file duration in seconds via a temporary HTML element. Returns 0 for non-media or on timeout. */
export function getMediaDuration(file: File, timeoutMs = 8000): Promise<number> {
  return new Promise((resolve) => {
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    if (!isVideo && !isAudio) { resolve(0); return; }
    let settled = false;
    const done = (v: number) => { if (settled) return; settled = true; clearTimeout(timer); URL.revokeObjectURL(url); resolve(v); };
    const el = document.createElement(isVideo ? 'video' : 'audio');
    const url = URL.createObjectURL(file);
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration : 0);
    el.onerror = () => done(0);
    const timer = setTimeout(() => done(0), timeoutMs);
    el.src = url;
  });
}

export function getMediaDurationFromUrl(url: string, mediaType: 'video' | 'audio', timeoutMs = 8000): Promise<number> {
  return new Promise((resolve) => {
    let settled = false;
    const el = document.createElement(mediaType);
    const done = (v: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.onloadedmetadata = null;
      el.onerror = null;
      el.src = '';
      resolve(v);
    };
    el.preload = 'metadata';
    el.crossOrigin = 'anonymous';
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration : 0);
    el.onerror = () => done(0);
    const timer = setTimeout(() => done(0), timeoutMs);
    el.src = url;
  });
}

const DEFAULT_FORMATS: Record<string, string[]> = {
  image: ['image/png', 'image/jpeg', 'image/webp'],
  video: ['video/mp4'],
  audio: ['audio/*'],
};

/** Build an HTML `accept` string based on the per-type config. */
export function buildAcceptFromConfig(cfg: InlineMediaConfig | undefined): string {
  if (!cfg?.allowInline) return '';
  const parts: string[] = [];
  for (const key of ['image', 'video', 'audio'] as const) {
    const tc = cfg[key];
    if (tc?.allow) parts.push(...(tc.formats?.length ? tc.formats : DEFAULT_FORMATS[key]));
  }
  return parts.join(',') || 'image/png,image/jpeg,image/webp,video/mp4,audio/*';
}

/**
 * Count inline attachments by type.
 * Returns { image: n, video: n, audio: n }.
 */
export function countByType(attachments: InlineAttachment[]): Record<InlineAttachment['fileType'], number> {
  const counts = { image: 0, video: 0, audio: 0 };
  for (const a of attachments) if (a.fileType in counts) counts[a.fileType]++;
  return counts;
}

/** Total capacity remaining across all types based on config + existing attachments + connected nodes. */
export function canAddMore(
  cfg: InlineMediaConfig | undefined,
  existing: InlineAttachment[],
  connectedCounts: { image?: number; video?: number; audio?: number } = {},
): boolean {
  if (!cfg?.allowInline) return false;
  const inline = countByType(existing);
  const imgTotal = inline.image + (connectedCounts.image ?? 0);
  const vidTotal = inline.video + (connectedCounts.video ?? 0);
  const audTotal = inline.audio + (connectedCounts.audio ?? 0);
  const total = imgTotal + vidTotal + audTotal;
  const mediaCount = vidTotal + audTotal;
  if (cfg.maxTotalCount != null && total >= cfg.maxTotalCount) return false;
  const imgOk = (cfg.image?.allow && imgTotal < (cfg.image.maxCount ?? Infinity));
  const mediaFull = cfg.maxMediaCount != null && mediaCount >= cfg.maxMediaCount;
  const vidOk = !mediaFull && (cfg.video?.allow && vidTotal < (cfg.video.maxCount ?? Infinity));
  const audOk = !mediaFull && (cfg.audio?.allow && audTotal < (cfg.audio.maxCount ?? Infinity));
  return Boolean(imgOk || vidOk || audOk);
}

export type MediaMeta = {
  duration?: number;
  width?: number;
  height?: number;
};

/**
 * Validate a single media item against type-level constraints.
 * Returns an error i18n key + params if invalid, or null if ok.
 * Shared by inline upload and edge validation paths.
 */
export function validateMediaItem(
  typeCfg: InlineMediaTypeConfig,
  mediaCfg: InlineMediaConfig | undefined,
  fileType: 'image' | 'video' | 'audio',
  meta: MediaMeta,
  existingMediaDuration: number,
): { key: string; params: Record<string, any> } | null {
  if (fileType !== 'image' && meta.duration != null && meta.duration > 0) {
    if (typeCfg.minDuration != null && meta.duration < parseSeconds(typeCfg.minDuration)) {
      return { key: 'File duration {{dur}}s is below minimum of {{min}}', params: { dur: Math.round(meta.duration * 10) / 10, min: typeCfg.minDuration } };
    }
    if (typeCfg.maxDuration != null && meta.duration > parseSeconds(typeCfg.maxDuration)) {
      return { key: 'File duration {{dur}}s exceeds maximum of {{max}}', params: { dur: Math.round(meta.duration * 10) / 10, max: typeCfg.maxDuration } };
    }
    if (mediaCfg?.mediaDurationLimit?.enabled) {
      const maxTotal = parseSeconds(mediaCfg.mediaDurationLimit.maxTotalDuration);
      if (existingMediaDuration + meta.duration > maxTotal) {
        return { key: 'Total media duration would exceed limit of {{max}}', params: { max: mediaCfg.mediaDurationLimit.maxTotalDuration } };
      }
    }
  }
  if (fileType === 'video' && (typeCfg.minPixels != null || typeCfg.maxPixels != null)) {
    const w = meta.width ?? 0;
    const h = meta.height ?? 0;
    if (w > 0 && h > 0) {
      const totalPixels = w * h;
      if (typeCfg.minPixels != null && totalPixels < typeCfg.minPixels) {
        return { key: 'Video resolution {{w}}×{{h}} ({{pixels}} pixels) is below minimum of {{min}} pixels', params: { w, h, pixels: totalPixels.toLocaleString(), min: typeCfg.minPixels.toLocaleString() } };
      }
      if (typeCfg.maxPixels != null && totalPixels > typeCfg.maxPixels) {
        return { key: 'Video resolution {{w}}×{{h}} ({{pixels}} pixels) exceeds limit of {{max}} pixels', params: { w, h, pixels: totalPixels.toLocaleString(), max: typeCfg.maxPixels.toLocaleString() } };
      }
    }
  }
  return null;
}
